import formFieldCopy from "../form-fields/copy.json";

export const ASC_CONTEXT_MESSAGE_TYPE = "octane:asc-context";
export const ASC_EVENT_MESSAGE_TYPE = "octane:asc-event";

const ASC_EVENT_NAMES = new Set([
    "asc_cta_interaction",
    "asc_form_engagement",
    "asc_form_submission",
    "asc_form_submission_sales",
    "asc_retail_process",
]);

const FIELD_LABELS = Object.freeze({
    first_name: formFieldCopy.first_name.label,
    last_name: formFieldCopy.last_name.label,
    email: formFieldCopy.email.label,
    date_of_birth: formFieldCopy.date_of_birth.label,
    street1: formFieldCopy.street1.label,
    street2: formFieldCopy.street2.label,
    city: formFieldCopy.city.label,
    state: formFieldCopy.state.label,
    zip_code: formFieldCopy.zip_code.label,
    residential_status: formFieldCopy.residential_status.label,
    residential_date: formFieldCopy.residential_date.label,
    employment_status: formFieldCopy.employment_status.label,
    yearly_income: formFieldCopy.yearly_income.label,
    contact_preference: formFieldCopy.contact_preference.label,
    phone_number: formFieldCopy.phone_number.label,
    purchase_intent: formFieldCopy.purchase_intent.label,
    citizenship_status: formFieldCopy.citizenship_status.label,
    monthly_housing_payment: formFieldCopy.monthly_housing_payment.label,
    employer_name: formFieldCopy.employer_name.label,
    employer_phone: formFieldCopy.employer_phone_number.label,
    job_title: formFieldCopy.job_title.label,
    employer_date: formFieldCopy.employer_date.label,
    social_security_number: "Social Security Number",
    trade_in_intent: formFieldCopy.trade_in_intent.label,
    coapplicant_intent: formFieldCopy.coapplicant_intent.label,
    relationship_to_primary: formFieldCopy.relationship_to_primary.label,
    reuse_primary_address: formFieldCopy.coapplicant_reuse_address.label,
    prequal_disclosure_consent: "Prequalification consent",
    text_consent: "Text message consent",
});

const ITEM_PARAMETERS = [
    "item_id",
    "item_number",
    "item_condition",
    "item_year",
    "item_make",
    "item_model",
    "item_price",
    "item_variant",
    "item_color",
    "item_type",
    "item_category",
    "item_inventory_date",
    "item_location",
];

const EVENT_PARAMETERS = Object.freeze({
    asc_form_engagement: [
        "event_owner",
        "page_type",
        "comm_type",
        "comm_status",
        "element_text",
        "department",
        "form_name",
        "form_type",
        "product_name",
        "element_subtype",
        "flow_name",
        ...ITEM_PARAMETERS,
        "page_location",
    ],
    asc_form_submission: [
        "event_owner",
        "page_type",
        "comm_type",
        "comm_outcome",
        "submission_id",
        "element_text",
        "department",
        "form_name",
        "form_type",
        "product_name",
        "element_subtype",
        "flow_name",
        ...ITEM_PARAMETERS.filter(parameter => parameter !== "item_location"),
        "page_location",
    ],
    asc_form_submission_sales: [
        "event_owner",
        "page_type",
        "comm_type",
        "comm_outcome",
        "submission_id",
        "element_text",
        "department",
        "form_name",
        "form_type",
        "product_name",
        "element_subtype",
        "flow_name",
        ...ITEM_PARAMETERS.filter(parameter => parameter !== "item_location"),
        "page_location",
    ],
    asc_retail_process: [
        "event_owner",
        "page_type",
        "flow_name",
        "element_title",
        "flow_outcome",
        "flow_stage",
        "product_name",
        "element_subtype",
        "item_payment",
        ...ITEM_PARAMETERS,
        "page_location",
    ],
});

const REQUIRED_EVENT_PARAMETERS = Object.freeze({
    asc_form_engagement: [
        "event_owner",
        "page_type",
        "comm_type",
        "comm_status",
        "element_text",
        "department",
        "form_name",
        "form_type",
        "page_location",
    ],
    asc_form_submission: [
        "event_owner",
        "page_type",
        "comm_type",
        "comm_outcome",
        "submission_id",
        "element_text",
        "department",
        "form_name",
        "form_type",
        "page_location",
    ],
    asc_form_submission_sales: [
        "event_owner",
        "page_type",
        "comm_type",
        "comm_outcome",
        "submission_id",
        "element_text",
        "department",
        "form_name",
        "form_type",
        "page_location",
    ],
    asc_retail_process: [
        "event_owner",
        "page_type",
        "flow_name",
        "element_title",
        "flow_outcome",
        "page_location",
    ],
});

const isPlainObject = value => (
    value !== null
    && typeof value === "object"
    && !Array.isArray(value)
);

const isValidEvent = event => (
    isPlainObject(event)
    && typeof event.event_id === "string"
    && event.event_id.length > 0
    && ASC_EVENT_NAMES.has(event.event_name)
    && isPlainObject(event.parameters)
);

export const isAscContextMessage = message => {
    if (!isPlainObject(message)
        || message.type !== ASC_CONTEXT_MESSAGE_TYPE
        || message.version !== 1
        || typeof message.context_id !== "string"
        || !message.context_id) {
        return false;
    }

    const payload = message.payload;
    return isPlainObject(payload)
        && payload.spec_version === "1.2"
        && ["direct", "partner_gtm"].includes(payload.delivery_mode)
        && ["item", "itemlist"].includes(payload.page_type)
        && typeof payload.page_location === "string"
        && /^https?:\/\//.test(payload.page_location)
        && isPlainObject(payload.common_parameters)
        && isPlainObject(payload.item_parameters)
        && Array.isArray(payload.initial_events)
        && payload.initial_events.every(isValidEvent);
};

const getContextParameters = ascContext => ({
    ...ascContext.payload.common_parameters,
    page_type: ascContext.payload.page_type,
    page_location: ascContext.payload.page_location,
    ...ascContext.payload.item_parameters,
});

const getCompleteEventParameters = (ascContext, event) => {
    const mergedParameters = {
        ...getContextParameters(ascContext),
        ...event.parameters,
    };
    const parameterList = EVENT_PARAMETERS[event.event_name];
    if (!parameterList) return mergedParameters;

    return parameterList.reduce((parameters, parameterName) => {
        parameters[parameterName] = mergedParameters[parameterName] ?? "";
        return parameters;
    }, {});
};

const hasRequiredEventParameters = (eventName, parameters) => (
    (REQUIRED_EVENT_PARAMETERS[eventName] || []).every(parameterName => (
        parameters[parameterName] !== ""
        && parameters[parameterName] !== null
        && parameters[parameterName] !== undefined
    ))
);

/**
 * Delivers an ASC event without performing network work or allowing analytics
 * failures to affect the prequalification flow.
 */
export const dispatchAscEvent = (partnerStore, event) => {
    try {
        if (!partnerStore?.isAutomobile || !partnerStore.ascContext || !isValidEvent(event)) {
            return false;
        }

        const parameters = getCompleteEventParameters(partnerStore.ascContext, event);
        if (!hasRequiredEventParameters(event.event_name, parameters)) {
            console.warn(`ASC event skipped: missing required parameters for ${event.event_name}`);
            return false;
        }

        partnerStore.ascEventIds = partnerStore.ascEventIds || new Set();
        if (partnerStore.ascEventIds.has(event.event_id)) return false;
        partnerStore.ascEventIds.add(event.event_id);
        const {delivery_mode: deliveryMode} = partnerStore.ascContext.payload;

        if (!partnerStore.widgetMessageSource?.postMessage || !partnerStore.widgetMessageOrigin) {
            return false;
        }
        partnerStore.widgetMessageSource.postMessage({
            type: ASC_EVENT_MESSAGE_TYPE,
            version: 1,
            context_id: partnerStore.ascContext.context_id,
            event_id: event.event_id,
            event_name: event.event_name,
            // The parent owns the trusted page and inventory context. Send only
            // event-specific values so it can validate and enrich them itself.
            parameters: event.parameters,
        }, partnerStore.widgetMessageOrigin);

        if (deliveryMode === "partner_gtm") {
            if (typeof window === "undefined") return true;
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                ...parameters,
                event: event.event_name,
            });
        }
        return true;
    } catch (error) {
        console.warn("ASC event delivery failed", error);
        return false;
    }
};

export const installAscContext = (partnerStore, message) => {
    try {
        if (!partnerStore?.isAutomobile
            || !partnerStore.widgetMessageOrigin
            || !partnerStore.widgetMessageSource
            || !isAscContextMessage(message)) {
            return false;
        }

        if (partnerStore.ascContext?.context_id !== message.context_id) {
            partnerStore.ascEventIds = new Set();
            partnerStore.ascEngagedFields = new Set();
        }
        partnerStore.ascContext = message;
        message.payload.initial_events.forEach(event => dispatchAscEvent(partnerStore, event));
        return true;
    } catch (error) {
        console.warn("ASC context installation failed", error);
        return false;
    }
};

const createEventId = (partnerStore, suffix) => (
    `${partnerStore.ascContext.context_id}:${suffix}`
);

const formParameters = {
    comm_type: "form",
    department: "sales",
    form_name: "Octane Automobile Prequalification",
    form_type: "finance/credit",
    flow_name: "soft_pull",
};

const retailParameters = {
    flow_name: "soft_pull",
    element_title: "Octane Automobile Prequalification",
};

export const recordAscFormEngagement = (partnerStore, fieldName, applicantRole = "primary") => {
    try {
        const label = FIELD_LABELS[fieldName];
        if (!partnerStore?.ascContext || !label) return false;

        partnerStore.ascEngagedFields = partnerStore.ascEngagedFields || new Set();
        const fieldKey = `${applicantRole}:${fieldName}`;
        if (partnerStore.ascEngagedFields.has(fieldKey)) return false;
        const isFirstEngagement = partnerStore.ascEngagedFields.size === 0;
        partnerStore.ascEngagedFields.add(fieldKey);

        dispatchAscEvent(partnerStore, {
            event_id: createEventId(partnerStore, `form:${fieldKey}`),
            event_name: "asc_form_engagement",
            parameters: {
                ...formParameters,
                comm_status: isFirstEngagement ? "start" : "engage",
                element_text: label,
                element_subtype: "input_field",
            },
        });
        if (isFirstEngagement) {
            dispatchAscEvent(partnerStore, {
                event_id: createEventId(partnerStore, "retail:proceed"),
                event_name: "asc_retail_process",
                parameters: {
                    ...retailParameters,
                    flow_outcome: "proceed",
                    flow_stage: "2",
                },
            });
        }
        return true;
    } catch (error) {
        console.warn("ASC form engagement failed", error);
        return false;
    }
};

export const recordAscSubmissionSuccess = (partnerStore, submissionId, submitButtonLabel) => {
    try {
        if (!partnerStore?.ascContext) return false;

        dispatchAscEvent(partnerStore, {
            event_id: createEventId(partnerStore, "retail:lead"),
            event_name: "asc_retail_process",
            parameters: {
                ...retailParameters,
                flow_outcome: "lead",
                flow_stage: "3",
            },
        });

        if (!Number.isInteger(submissionId)) {
            console.warn("ASC form submission skipped: missing numeric submission id");
            return false;
        }

        const elementText = typeof submitButtonLabel === "string" && submitButtonLabel.trim()
            ? submitButtonLabel.trim()
            : "Show Me Offers";
        const parameters = {
            ...formParameters,
            comm_outcome: "opportunity",
            submission_id: submissionId,
            element_text: elementText,
            element_subtype: "cta_button",
        };
        ["asc_form_submission", "asc_form_submission_sales"].forEach(eventName => {
            dispatchAscEvent(partnerStore, {
                event_id: createEventId(partnerStore, `${eventName}:${submissionId}`),
                event_name: eventName,
                parameters,
            });
        });
        return true;
    } catch (error) {
        console.warn("ASC submission event failed", error);
        return false;
    }
};

export const recordAscRetailOutcome = (partnerStore, outcome, eventIdSuffix = outcome) => {
    if (!partnerStore?.ascContext || !["approved", "declined", "error"].includes(outcome)) {
        return false;
    }
    return dispatchAscEvent(partnerStore, {
        event_id: createEventId(partnerStore, `retail:${eventIdSuffix}`),
        event_name: "asc_retail_process",
        parameters: {
            ...retailParameters,
            flow_outcome: outcome,
            flow_stage: outcome === "error" ? "3" : "4",
            element_subtype: "",
            item_payment: "",
        },
    });
};
